import { toolCallStore, type ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";

export interface AnnualFollowupEntry {
  toolCallId: string;
  started: StartedFollowup[];
}

export interface StartedFollowup {
  clientKey: string;
  prepareKey: string;
}

export function openAnnualFollowup(toolCallId: string): void {
  STORE.open({ toolCallId, started: [] });
}

export function annualFollowupAnswered(toolCallId: string): void {
  STORE.answered(toolCallId);
}

export function hasAnnualFollowup(toolCallId: string): boolean {
  return STORE.has(toolCallId);
}

export function useAnnualFollowup(toolCallId: string): AnnualFollowupEntry | null {
  return STORE.useEntry(toolCallId);
}

export function useAnnualFollowupAnswering(toolCallId: string): boolean {
  return STORE.useAnswering(toolCallId);
}

export function useAnnualFollowupStages(): (toolCallId: string) => ChatCardStage | null {
  return STORE.useStages();
}

export function startFollowup(toolCallId: string, clientKey: string): string | null {
  const known = STORE.read(toolCallId);
  if (known === null || known.started.some((started) => started.clientKey === clientKey)) {
    return null;
  }
  const prepareKey = `${toolCallId}:${clientKey}`;
  STORE.update(toolCallId, (entry) => ({
    ...entry,
    started: [...entry.started, { clientKey, prepareKey }],
  }));
  return prepareKey;
}

function narrowEntry(value: unknown): AnnualFollowupEntry | null {
  if (!isRecord(value) || typeof value.toolCallId !== "string") return null;
  const started = Array.isArray(value.started) ? value.started.flatMap(startedOf) : [];
  return { toolCallId: value.toolCallId, started };
}

function startedOf(value: unknown): StartedFollowup[] {
  if (!isRecord(value)) return [];
  const { clientKey, prepareKey } = value;
  if (typeof clientKey !== "string" || clientKey === "") return [];
  if (typeof prepareKey !== "string" || prepareKey === "") return [];
  return [{ clientKey, prepareKey }];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const STORE = toolCallStore<AnnualFollowupEntry>({
  key: "simon.clients.annualFollowup",
  narrow: narrowEntry,
  stage: () => "done",
});
